import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { authService } from '../services/authService';

interface AuthState {
  accessCode: string | null;
  isAuthenticated: boolean;
  isVerifying: boolean;
  verificationError: string | null;
}

const initialState: AuthState = {
  accessCode: null,
  isAuthenticated: false,
  isVerifying: false,
  verificationError: null,
};

// Async thunk to verify access code
export const verifyAccessCode = createAsyncThunk(
  'auth/verifyAccessCode',
  async (code: string, { rejectWithValue }) => {
    try {
      const data = await authService.verifyAccessCode(code);
      
      if (!data.valid) {
        if (data.status === 'inactive') {
          return rejectWithValue('Code is inactive');
        }
        if (data.status === 'invalid_format') {
          return rejectWithValue('Invalid code format');
        }
        return rejectWithValue('Invalid code');
      }

      return code;
    } catch (error: any) {
      if (error.status === 404) {
        return rejectWithValue('Code not found');
      }
      return rejectWithValue(error.message || 'Network error while verifying code');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.accessCode = null;
      state.isAuthenticated = false;
      state.verificationError = null;
    },
    clearError: (state) => {
      state.verificationError = null;
    },
    // Load code from localStorage on startup
    loadStoredCode: (state, action: PayloadAction<string>) => {
      state.accessCode = action.payload;
      state.isAuthenticated = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(verifyAccessCode.pending, (state) => {
        state.isVerifying = true;
        state.verificationError = null;
      })
      .addCase(verifyAccessCode.fulfilled, (state, action) => {
        state.isVerifying = false;
        state.accessCode = action.payload;
        state.isAuthenticated = true;
        state.verificationError = null;
      })
      .addCase(verifyAccessCode.rejected, (state, action) => {
        state.isVerifying = false;
        state.accessCode = null;
        state.isAuthenticated = false;
        state.verificationError = action.payload as string;
      });
  },
});

export const { logout, clearError, loadStoredCode } = authSlice.actions;
export default authSlice.reducer;